// src/components/Store/StoreHeader.jsx
// Barra de la tienda estilo marketplace: buscador con selector de
// departamento, botón de carrito con contador y franja de categorías.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MagnifyingGlassIcon, ShoppingCartIcon, TruckIcon } from '@heroicons/react/24/outline';

// Sin onSearch/onCategory (página de producto) el buscador lleva a /store con la búsqueda.
const StoreHeader = ({ search = '', category = '', categories, cartCount, onOpenCart, onSearch, onCategory }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [draft, setDraft] = useState(search);
  const [draftCategory, setDraftCategory] = useState(category);

  // Si la búsqueda cambia desde fuera (URL, "quitar filtros"), refleja el cambio
  useEffect(() => setDraft(search), [search]);
  useEffect(() => setDraftCategory(category), [category]);

  const goToResults = (q, cat) => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (cat) params.set('category', cat);
    navigate(`/store${params.toString() ? `?${params}` : ''}`);
  };

  const submit = (e) => {
    e.preventDefault();
    const q = draft.trim();
    if (onSearch) {
      onSearch(q);
      onCategory?.(draftCategory);
    } else goToResults(q, draftCategory);
  };

  const pickCategory = (cat) => {
    setDraftCategory(cat);
    if (onCategory) onCategory(cat);
    else goToResults(draft.trim(), cat);
  };

  return (
    <div>
      {/* Buscador */}
      <div className="bg-[#7A0001]">
        <div className="mx-auto flex max-w-7xl items-center gap-2 px-3 py-2.5 sm:gap-3 sm:px-6 lg:px-8">
          <form onSubmit={submit} className="flex h-11 flex-1 overflow-hidden rounded-lg bg-white focus-within:ring-[3px] focus-within:ring-[#EFE6DD]/60">
            <select
              value={draftCategory}
              onChange={(e) => pickCategory(e.target.value)}
              aria-label={t('store.department')}
              className="hidden max-w-[11rem] shrink-0 cursor-pointer border-r border-[#3A2A20]/15 bg-[#EFE6DD] px-3 text-xs font-medium text-[#3A2A20] focus:outline-none sm:block"
            >
              <option value="">{t('store.categories.all')}</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {t(`store.categories.${c}`)}
                </option>
              ))}
            </select>
            <input
              type="search"
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                if (onSearch && e.target.value === '') onSearch(''); // limpiar con la "x" del input
              }}
              placeholder={t('store.searchPlaceholderLong')}
              aria-label={t('store.searchPlaceholder')}
              className="min-w-0 flex-1 px-3 text-base text-[#3A2A20] placeholder:text-[#3A2A20]/40 focus:outline-none sm:text-sm"
            />
            <button
              type="submit"
              className="flex w-12 shrink-0 items-center justify-center bg-[#EFE6DD] text-[#9A0002] transition hover:bg-white sm:w-14"
            >
              <span className="sr-only">{t('store.searchButton')}</span>
              <MagnifyingGlassIcon className="h-6 w-6" strokeWidth={2} aria-hidden="true" />
            </button>
          </form>

          <button
            type="button"
            onClick={onOpenCart}
            className="relative flex h-11 shrink-0 items-center gap-1.5 rounded-lg px-2 text-[#EFE6DD] transition hover:bg-[#9A0002] sm:px-3"
          >
            <span className="relative">
              <ShoppingCartIcon className="h-8 w-8" aria-hidden="true" />
              <span className="absolute -top-1.5 left-1/2 flex h-5 min-w-[1.25rem] -translate-x-1/3 items-center justify-center rounded-full bg-[#EFE6DD] px-1 text-xs font-bold text-[#9A0002]">
                {cartCount}
              </span>
            </span>
            <span className="hidden text-sm font-semibold sm:inline">{t('store.cart')}</span>
          </button>
        </div>
      </div>

      {/* Franja de departamentos */}
      <nav aria-label={t('store.department')} className="bg-[#9A0002] shadow-md">
        <div className="no-scrollbar mx-auto flex max-w-7xl items-center gap-1 overflow-x-auto px-3 sm:px-6 lg:px-8">
          {['', ...categories].map((c) => {
            const active = category === c;
            return (
              <button
                key={c || 'all'}
                type="button"
                onClick={() => pickCategory(c)}
                className={`shrink-0 whitespace-nowrap border-b-2 px-3 py-2 text-sm transition ${
                  active
                    ? 'border-[#EFE6DD] font-semibold text-white'
                    : 'border-transparent text-[#EFE6DD]/85 hover:border-[#EFE6DD]/50 hover:text-white'
                }`}
              >
                {c ? t(`store.categories.${c}`) : t('store.categories.all')}
              </button>
            );
          })}
          <span className="ml-auto hidden shrink-0 items-center gap-1.5 whitespace-nowrap py-2 pl-4 text-xs font-medium text-[#EFE6DD] md:flex">
            <TruckIcon className="h-4 w-4" aria-hidden="true" />
            {t('store.promo')}
          </span>
        </div>
      </nav>
    </div>
  );
};

export default StoreHeader;
